/**
 * TRUS-702: a small pill saying how a message in a member DM travelled
 * (Portal / SMS / Email / Call / Voicemail / Note / Campaign — TRUS-703), and — for an outbound SMS
 * or email — how delivery went (Sending / Sent / Delivered / Failed + why).
 *
 * Both render nothing when the server did not say (older backend), and the
 * channel badge renders nothing for staff chat / system posts.
 */
import { AlertTriangle, Check, CheckCheck, Clock, Globe, Mail, Megaphone, Phone, Smartphone, StickyNote, Voicemail } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';
import type { DeliveryStatus, MessageChannel } from '@/services/messagingApi';
import { blockReasonText, channelLabel, normalizeChannel } from './replyChannel';

const ICONS: Partial<Record<MessageChannel, typeof Globe>> = {
  portal: Globe,
  sms: Smartphone,
  email: Mail,
  call: Phone,
  voicemail: Voicemail,
  note: StickyNote,
  campaign: Megaphone,
};

const STYLES: Partial<Record<MessageChannel, string>> = {
  portal: 'border-teal-300 bg-teal-50 text-teal-700 dark:border-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  sms: 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  email: 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  call: 'border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  voicemail: 'border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  note: 'border-yellow-400 bg-yellow-50 text-yellow-800 dark:border-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-200',
  campaign: 'border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
};

interface MessageChannelBadgeProps {
  /** The server's channel value; capitalised legacy names are accepted. */
  channel: unknown;
  className?: string;
}

export function MessageChannelBadge({ channel, className }: MessageChannelBadgeProps) {
  const { t } = useAppTranslation();
  const c = normalizeChannel(channel);
  if (!c) return null;
  const label = channelLabel(c, t);
  if (!label) return null;
  const Icon = ICONS[c];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-1.5 py-0 text-[10px] font-medium leading-4',
        STYLES[c],
        className,
      )}
      data-testid="message-channel-badge"
      data-channel={c}
    >
      {Icon && <Icon className="h-3 w-3" aria-hidden="true" />}
      {label}
    </span>
  );
}

interface DeliveryStatusBadgeProps {
  status: DeliveryStatus | null | undefined;
  /** On a blue (own) bubble, use light text. */
  inverted?: boolean;
  className?: string;
}

export function DeliveryStatusBadge({ status, inverted = false, className }: DeliveryStatusBadgeProps) {
  const { t } = useAppTranslation();
  if (!status || !status.state) return null;
  const failed = status.state === 'failed' || status.state === 'blocked';
  let label: string;
  let Icon = Check;
  switch (status.state) {
    case 'queued':
      label = t('messaging.delivery.queued', 'Sending');
      Icon = Clock;
      break;
    case 'sent':
      label = t('messaging.delivery.sent', 'Sent');
      break;
    case 'delivered':
      label = t('messaging.delivery.delivered', 'Delivered');
      Icon = CheckCheck;
      break;
    case 'blocked':
      label = t('messaging.delivery.blocked', 'Not sent');
      Icon = AlertTriangle;
      break;
    case 'failed':
    default:
      label = t('messaging.delivery.failed', 'Failed');
      Icon = AlertTriangle;
      break;
  }
  // The error is either a reason code (opted_out, …) or provider text.
  const reason = failed && status.error ? reasonOrText(status.error, t) : null;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-[11px]',
        failed
          ? inverted
            ? 'font-medium text-red-100'
            : 'font-medium text-red-600 dark:text-red-400'
          : inverted
            ? 'text-white/80'
            : 'text-muted-foreground',
        className,
      )}
      data-testid="delivery-status"
      data-state={status.state}
      title={reason ?? undefined}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {reason ? t('messaging.delivery.withReason', '{{status}}: {{reason}}', { status: label, reason }) : label}
    </span>
  );
}

const REASON_CODES = new Set([
  'opted_out',
  'no_phone',
  'no_email',
  'no_portal_login',
  'sender_not_configured',
  'consent_missing',
]);

function reasonOrText(error: string, t: Parameters<typeof blockReasonText>[1]): string {
  return REASON_CODES.has(error) ? blockReasonText(error, t) : error;
}
